import {ref, onMounted, onUnmounted, computed, nextTick, watch} from 'vue'
import {Keyboard} from '@capacitor/keyboard'
import {useScreenSafeArea} from '@vueuse/core'
import {useCurrentAppPlatform} from '../modules/platform/composables/useCurrentAppPlatform'
import {createGlobalState} from '@vueuse/core'

function parsePx(value: string) {
  if (value == null) return 0
  if (typeof value === 'number') return value
  if (typeof value === 'string') {
    const m = value.match(/^(\d+(\.\d+)?)/)
    return m ? parseFloat(m[0]) : 0
  }
  return 0
}

export const useKeyboardSafeArea = createGlobalState(() => {
  const {top, bottom, left, right, update} = useScreenSafeArea()
  const {isAppPlatformMobile} = useCurrentAppPlatform()

  const keyboardHeight = ref(0)
  const lastKnownKeyboardHeight = ref(0)
  const isKeyboardVisible = ref(false)
  const isKeyboardSettled = ref(false)
  const emojiPanelHeight = ref(0)
  const isEmojiPanelOpen = ref(false)
  const isKeyboardHandoff = ref(false)
  const handoffHeight = ref(0)
  let handoffTimer: ReturnType<typeof setTimeout> | null = null
  let onVisualViewportResize: (() => void) | null = null

  const bottomAsNumber = computed(() => parsePx(bottom.value))
  const topAsNumber = computed(() => parsePx(top.value))
  const leftAsNumber = computed(() => parsePx(left.value))
  const rightAsNumber = computed(() => parsePx(right.value))

  const visibleKeyboardHeight = computed(() => {
    if(isEmojiPanelOpen.value && emojiPanelHeight.value > 0) return emojiPanelHeight.value
    if(isKeyboardVisible.value) return keyboardHeight.value
    if(isKeyboardHandoff.value && handoffHeight.value > 0) return handoffHeight.value
    return 0
  })

  const isBottomPanelVisible = computed(() => {
    return isKeyboardVisible.value || isEmojiPanelOpen.value || isKeyboardHandoff.value
  })

  const beginKeyboardHandoff = () => {
    if(isKeyboardVisible.value) return
    handoffHeight.value = visibleKeyboardHeight.value
    if(handoffHeight.value <= 0) return
    isKeyboardHandoff.value = true
    if(handoffTimer) clearTimeout(handoffTimer)
    handoffTimer = setTimeout(() => {
      isKeyboardHandoff.value = false
    }, 1000)
  }

  watch(isKeyboardVisible, (visible) => {
    if(visible) {
      if(handoffTimer) clearTimeout(handoffTimer)
      isKeyboardHandoff.value = false
    }
  })

  const bottomWithKeyboard = computed(() => {
    return visibleKeyboardHeight.value + bottomAsNumber.value
  })

  onMounted(async () => {
    await nextTick()
    update()

    // // Also update on resize and orientation changes to handle dynamic changes
    // useEventListener(window, 'resize', () => {
    //   update()
    // })

    // useEventListener(window, 'orientationchange', () => {
    //   // Small delay for orientation change to complete before reading new values
    //   setTimeout(() => {
    //     update()
    //   }, 100)

    if (isAppPlatformMobile) {
      Keyboard.addListener('keyboardWillShow', info => {
        keyboardHeight.value = info.keyboardHeight
        lastKnownKeyboardHeight.value = info.keyboardHeight
        isKeyboardVisible.value = true
      })

      Keyboard.addListener('keyboardDidShow', info => {
        keyboardHeight.value = info.keyboardHeight
        lastKnownKeyboardHeight.value = info.keyboardHeight
        isKeyboardVisible.value = true
        isKeyboardSettled.value = true
      })

      Keyboard.addListener('keyboardWillHide', () => {
        keyboardHeight.value = 0
        isKeyboardVisible.value = false
        isKeyboardSettled.value = false
      })

      Keyboard.addListener('keyboardDidHide', () => {
        keyboardHeight.value = 0
        isKeyboardVisible.value = false
        isKeyboardSettled.value = false
      })

      const visualViewport = window.visualViewport
      if(visualViewport) {
        onVisualViewportResize = () => {
          if(!isKeyboardSettled.value) {
            return
          }
          const overlap = Math.max(0, window.innerHeight - visualViewport.height - visualViewport.offsetTop)
          if(overlap > 0) {
            keyboardHeight.value = overlap
            lastKnownKeyboardHeight.value = overlap
          }
        }
        visualViewport.addEventListener('resize', onVisualViewportResize)
      }
    }
  })
  
  onUnmounted(() => {
    if (isAppPlatformMobile) {
      Keyboard.removeAllListeners()
    }
    if (onVisualViewportResize && window.visualViewport) {
      window.visualViewport.removeEventListener('resize', onVisualViewportResize)
      onVisualViewportResize = null
    }
    if (handoffTimer) {
      clearTimeout(handoffTimer)
      handoffTimer = null
    }
  })

  return {
    top: topAsNumber,
    left: leftAsNumber,
    right: rightAsNumber,
    bottom: bottomAsNumber,
    bottomWithKeyboard,
    keyboardHeight,
    lastKnownKeyboardHeight,
    isKeyboardVisible,
    isKeyboardSettled,
    isBottomPanelVisible,
    visibleKeyboardHeight,
    emojiPanelHeight,
    isEmojiPanelOpen,
    beginKeyboardHandoff,
  }
})
